import { useEffect, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2 } from "lucide-react";

const EMPTY = { account_holder_name: "", bank_name: "", account_number: "", ifsc: "" };

export default function AdminBankTab() {
  const [form, setForm] = useState(EMPTY);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/bank-account");
      if (data && data.account_number) {
        setForm({
          account_holder_name: data.account_holder_name || "",
          bank_name: data.bank_name || "",
          account_number: data.account_number || "",
          ifsc: data.ifsc || "",
        });
        setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
      } else {
        setForm(EMPTY); setMeta(null);
      }
    } catch {
      toast.error("Could not load bank details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.account_holder_name.trim()) return toast.error("Account holder name is required");
    if (!form.bank_name.trim()) return toast.error("Bank name is required");
    const acct = form.account_number.replace(/\s/g, "");
    if (!/^\d{9,18}$/.test(acct)) return toast.error("Account number must be 9 to 18 digits");
    if (!/^[A-Za-z]{4}0[A-Za-z0-9]{6}$/.test(form.ifsc.trim())) return toast.error("Invalid IFSC format");
    setBusy(true);
    try {
      await api.put("/admin/platform/bank-account", {
        account_holder_name: form.account_holder_name.trim(),
        bank_name: form.bank_name.trim(),
        account_number: acct,
        ifsc: form.ifsc.trim().toUpperCase(),
      });
      toast.success("Bank details saved");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-gray-500 py-8">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="admin-bank-tab">
      <div>
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-[#F48E2E]" />
          <div className="font-display text-2xl">Bank Account</div>
        </div>
        <p className="text-xs text-gray-500 mt-1">CityShor's settlement bank account. Vendors see this in their payout section so they know where to transfer funds. Keep it accurate — it's auditable.</p>
      </div>

      <div className="p-5 bg-white border border-gray-200 rounded-2xl space-y-3 max-w-2xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Account holder name</Label>
            <Input data-testid="bank-holder" value={form.account_holder_name} onChange={(e) => set("account_holder_name", e.target.value)} placeholder="Cityshor LLP" />
          </div>
          <div>
            <Label>Bank name</Label>
            <Input data-testid="bank-name" value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} placeholder="AU SMALL FINANCE BANK LTD" />
          </div>
          <div>
            <Label>Account number</Label>
            <Input data-testid="bank-account" inputMode="numeric" maxLength={18} value={form.account_number} onChange={(e) => set("account_number", e.target.value.replace(/\D/g, "").slice(0, 18))} placeholder="2602253424427202" />
          </div>
          <div>
            <Label>IFSC</Label>
            <Input data-testid="bank-ifsc" maxLength={11} pattern="[A-Za-z]{4}0[A-Za-z0-9]{6}" value={form.ifsc} onChange={(e) => set("ifsc", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11))} placeholder="AUBL0002534" />
          </div>
        </div>

        {meta?.updated_at && (
          <div className="text-[11px] text-gray-500" data-testid="bank-last-updated">
            Last updated {new Date(meta.updated_at).toLocaleString("en-IN")}{meta.updated_by ? ` by ${meta.updated_by}` : ""}
          </div>
        )}

        <Button data-testid="bank-save" onClick={save} disabled={busy} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white">
          {busy ? "Saving…" : "Save bank details"}
        </Button>
      </div>
    </div>
  );
}
